import { Box, Card, IconButton, Stack, Tooltip, Typography } from "@mui/material";

import React, { useState } from "react";
import { MoreVert, OpenInNew, VolumeOff, VolumeUp } from "@mui/icons-material";

const AdBanner = ({
     adText = "This is an Ad",
     adDescription = "No description available for this ad.",
     adImage,
     adVideo,
     adLink,
     adSponseredBy = "Your Company",
     asIsSponsered = false,
     adThumbnail = "",
     width,
     height,
     fullWidth = false,
     isMobile = false,
}: {
     adText?: string;
     adDescription?: string;
     adImage?: string;
     adVideo?: string;
     adLink?: string;
     adSponseredBy?: string;
     asIsSponsered?: boolean;
     adThumbnail?: string;
     width?: any;
     height?: any;
     fullWidth?: boolean;
     isMobile?: boolean;
}) => {
     const [muted, setMuted] = useState(true);
     const [videoLoaded, setVideoLoaded] = useState(false);

     // Wrapper for ad click
     const handleAdClick = () => {
          if (adLink) {
               window.open(adLink, "_blank");
          }
     };

     return (
          <Card
               sx={{
                    width: isMobile || fullWidth ? "100%" : width,
                    height: fullWidth ? "auto" : height,
                    position: "relative",
                    display: "flex",
                    flexDirection: fullWidth ? "row" : "column",
                    justifyContent: fullWidth ? "initial" : "space-between",
                    overflow: "hidden",
                    cursor: "pointer",
               }}
               onClick={handleAdClick}
          >
               <Box
                    sx={{
                         width: fullWidth ? 250 : "100%",
                         aspectRatio: "16/9",
                         backgroundColor: "#f2f2f2",
                         position: "relative",
                         display: "flex",
                         alignItems: "center",
                         justifyContent: "center",
                         fontSize: "1rem",
                         color: "#666",
                         fontWeight: 500,
                    }}
               >
                    {adVideo ? (
                         <>
                              {/* Show thumbnail image as placeholder until video loads */}
                              {!videoLoaded && adThumbnail && (
                                   <img
                                        src={adThumbnail}
                                        alt="ad preview"
                                        style={{
                                             width: "100%",
                                             height: "100%",
                                             objectFit: "cover",
                                             position: "absolute",
                                             top: 0,
                                             left: 0,
                                             borderRadius: 8,
                                             zIndex: 1,
                                        }}
                                   />
                              )}

                              <video
                                   src={adVideo}
                                   autoPlay
                                   muted={muted}
                                   loop
                                   playsInline
                                   onLoadedData={() => setVideoLoaded(true)}
                                   style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius: 8,
                                        zIndex: 2,
                                        position: "relative",
                                        pointerEvents: "none",
                                   }}
                              />
                              <IconButton
                                   onClick={(e) => {
                                        e.stopPropagation();
                                        setMuted((prev) => !prev);
                                   }}
                                   sx={{
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        color: "#fff",
                                        zIndex: 3,
                                        "&:hover": {
                                             background: "rgba(0,0,0,0.7)",
                                        },
                                   }}
                              >
                                   {muted ? <VolumeOff /> : <VolumeUp />}
                              </IconButton>
                         </>
                    ) : (
                         <Box
                              sx={{
                                   width: "100%",
                                   height: "100%",
                                   position: "relative",
                                   overflow: "hidden",
                                   backgroundColor: "#000", // in case image has transparent area
                              }}
                         >
                              <img
                                   src={adImage}
                                   alt="Advertisement"
                                   style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "fill",
                                        borderRadius: 0,
                                   }}
                              />

                              <IconButton
                                   sx={{
                                        position: "absolute",
                                        bottom: 8,
                                        right: 8,
                                        color: "#fff",
                                        backgroundColor: "rgba(0,0,0,0.4)",
                                        zIndex: 4,
                                        "&:hover": {
                                             backgroundColor: "rgba(0,0,0,0.7)",
                                        },
                                   }}
                                   size="small"
                              >
                                   <OpenInNew fontSize="small" />
                              </IconButton>
                         </Box>
                    )}
               </Box>

               <Box sx={{ p: fullWidth ? 1 : 2, marginLeft: fullWidth ? 3 : "auto", width: "100%" }}>
                    <Typography variant="h6"
                         // noWrap
                         sx={{
                              whiteSpace: "normal",
                              overflow: "visible",
                              textOverflow: "unset",
                              lineHeight: "1.5rem",
                              wordBreak: "break-word",
                         }}
                    >
                         {adText}
                    </Typography>
                    <Typography display="block" variant="caption" color="text.secondary"
                         // sx={{ minHeight: '3rem', lineHeight: '1.5rem', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', }}
                         sx={{
                              lineHeight: '1.5rem',
                              whiteSpace: "normal",
                              overflow: "visible",
                              textOverflow: "unset",
                         }}
                    >
                         {adDescription}
                    </Typography>

                    <Box sx={{ mt: 1, minHeight: '1.5rem' }}>
                         {asIsSponsered && (
                              <Stack
                                   direction={"row"}
                                   alignItems={"center"}
                                   justifyContent={"space-between"}
                              >
                                   <Typography
                                        variant="caption"
                                        color="text.secondary"
                                   >
                                        {`Sponsored Content • ${adSponseredBy}`}
                                   </Typography>
                              </Stack>
                         )}
                    </Box>

               </Box>
          </Card>
     );
};

export default AdBanner;
